import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { Typography } from 'common/components/Typography';
import { IEmailTypeEnum, SendEmailDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';

interface IProps {
    open?: boolean;
    userIds?: string[];
    onClose?: () => void;
    onComplete?: () => void;
}

export const AcademyInviteEmailDialog = ({
    open,
    userIds,
    onClose,
    onComplete,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [sendEmail, { loading: loadingSendEmail }] =
        useMutation(SendEmailDocument);

    const count = userIds?.length || 0;

    const handleSendEmail = async () => {
        if (!userIds?.length) return;

        let result;

        try {
            result = await sendEmail({
                variables: {
                    emailType: IEmailTypeEnum.AcademyInvite,
                    userIds,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        const { succeededIds = [], failedIds = [] } =
            result.data?.sendEmail?.response || {};

        if (succeededIds.length) {
            displaySnackbar(
                translate('academyInviteEmail.academyInviteEmailSent', {
                    count: succeededIds.length,
                }),
                {
                    variant: 'success',
                }
            );
        }

        if (failedIds.length) {
            displaySnackbar(
                translate('academyInviteEmail.academyInviteEmailFailed', {
                    count: failedIds.length,
                }),
                { variant: 'error' }
            );
        }

        onComplete?.();

        onClose?.();
    };

    const title = translate('academyInviteEmail.dialog.title', {
        count,
    });
    const text = translate('academyInviteEmail.dialog.text', {
        count,
    });

    return (
        <ConfirmDialog
            confirmText={translate('academyInviteEmail.dialog.buttonText', {
                count,
            })}
            loading={loadingSendEmail}
            open={!!open}
            title={title}
            onCancel={() => onClose?.()}
            onClose={() => onClose?.()}
            onConfirm={handleSendEmail}
        >
            <Typography>{text}</Typography>
        </ConfirmDialog>
    );
};
